/**
 * Ad Tracking — fires conversion events to Google Ads (gtag) + Meta Pixel (fbq)
 *
 * Google Ads: AW-17880500690
 * Meta Pixel: 787507227751405
 *
 * UTM parameters are captured on first page load and stored in sessionStorage.
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
  }
}

// ─── UTM Capture ───────────────────────────────────────────────────────

export function captureUTMParams(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    const utm: Record<string, string> = {};

    utmKeys.forEach(key => {
      const val = params.get(key);
      if (val) utm[key] = val;
    });

    if (Object.keys(utm).length > 0) {
      sessionStorage.setItem('onrecord_utm', JSON.stringify(utm));
      sessionStorage.setItem('onrecord_landing_url', window.location.href);
    }

    // Also store referrer if present
    if (document.referrer && !document.referrer.includes('onrecord.live')) {
      sessionStorage.setItem('onrecord_referrer', document.referrer);
    }
  } catch {
    // sessionStorage may be blocked in some browsers
  }
}

export function getUTMParams(): Record<string, string> {
  try {
    const stored = sessionStorage.getItem('onrecord_utm');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// ─── Event Helpers ─────────────────────────────────────────────────────

function gtagEvent(eventName: string, params?: Record<string, any>): void {
  if (window.gtag) {
    window.gtag('event', eventName, { ...params, ...getUTMParams() });
  }
}

function fbqEvent(eventName: string, params?: Record<string, any>): void {
  if (window.fbq) {
    window.fbq('track', eventName, params);
  }
}

// ─── Conversion Events ────────────────────────────────────────────────

/** User completed signup (OTP verified or OAuth success) */
export function trackSignUp(method: string): void {
  gtagEvent('sign_up', { method });
  fbqEvent('CompleteRegistration', { content_name: method });
}

/** User completed profile setup (onboarding done) */
export function trackOnboardingComplete(username: string): void {
  gtagEvent('onboarding_complete', { username });
  fbqEvent('Lead', { content_name: 'onboarding_complete' });
}

/** User clicked app download link */
export function trackAppDownloadClick(platform: 'ios' | 'android'): void {
  gtagEvent('app_download_click', {
    platform,
    event_category: 'engagement',
    event_label: platform === 'ios' ? 'App Store' : 'Play Store',
  });
  fbqEvent('InitiateCheckout', { content_name: `app_download_${platform}` });
}

/** User viewed a public post (shared link) */
export function trackPostView(postId: string): void {
  gtagEvent('post_view', { post_id: postId, event_category: 'content' });
  fbqEvent('ViewContent', { content_ids: [postId], content_type: 'post' });
}

/** User logged in */
export function trackLogin(method: string): void {
  gtagEvent('login', { method });
  // No fbq equivalent needed — login is not a standard conversion
}

/** User performed a search */
export function trackSearch(query: string): void {
  gtagEvent('search', { search_term: query });
  fbqEvent('Search', { search_string: query });
}

/** User created a post (web) */
export function trackPostCreated(): void {
  gtagEvent('post_created', { event_category: 'content' });
  fbqEvent('Purchase', { content_name: 'post_created', value: 0, currency: 'USD' });
}
